import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/gbl-logo.png';

// Bank logos
import hdfc from '../Banks/Hdfc.png';
import icici from '../Banks/Icici.png';
import auxilo from '../Banks/auxilo.jpg';
import avanse from '../Banks/avanse.jpg';
import axisBank from '../Banks/axis bank.jpg';
import idfc from '../Banks/idfc.png';
import incred from '../Banks/incred.jpg';
import pmb from '../Banks/pmb.png';
import sbi from '../Banks/sbi.jpg';
import unionBank from '../Banks/union.png';
import yesBank from '../Banks/yes bank.png';

const BANK_LOGOS = [
  { src: hdfc, alt: 'HDFC Bank' },
  { src: icici, alt: 'ICICI Bank' },
  { src: sbi, alt: 'SBI' },
  { src: axisBank, alt: 'Axis Bank' },
  { src: idfc, alt: 'IDFC First' },
  { src: yesBank, alt: 'Yes Bank' },
  { src: unionBank, alt: 'Union Bank' },
  { src: avanse, alt: 'Avanse' },
  { src: auxilo, alt: 'Auxilo' },
  { src: incred, alt: 'InCred' },
  { src: pmb, alt: 'PMB' },
];

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function BankLogoScroller() {
  // Duplicate logos for seamless infinite scroll
  const logos = [...BANK_LOGOS, ...BANK_LOGOS];

  return (
    <div className="w-full overflow-hidden py-4">
      <p className="text-xs font-medium text-[#5F6368] uppercase tracking-wider text-center mb-4">
        Our Tie-Up Bank Partners
      </p>
      <div className="relative">
        <div
          className="flex items-center gap-8 animate-scroll"
          style={{
            width: 'max-content',
            animation: 'scroll 25s linear infinite',
          }}
        >
          {logos.map((bank, i) => (
            <div
              key={i}
              className="flex-shrink-0 w-20 h-12 bg-white rounded-lg border border-[#E8EAED] flex items-center justify-center p-2"
            >
              <img
                src={bank.src}
                alt={bank.alt}
                className="max-w-full max-h-full object-contain"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

const FEATURES = [
  {
    icon: (
      <svg className="w-5 h-5 text-[#1A73E8]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    title: 'Live Status Tracking',
    desc: 'Know exactly where your loan application stands in real-time',
  },
  {
    icon: (
      <svg className="w-5 h-5 text-[#1E8E3E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
      </svg>
    ),
    title: 'Secure Document Upload',
    desc: 'Upload all required documents securely in one place',
  },
  {
    icon: (
      <svg className="w-5 h-5 text-[#F9AB00]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    title: 'Fast 3-Day Approval',
    desc: 'Get your education loan approved in as fast as 3 working days',
  },
];

export default function SignInPage() {
  const { signIn, authError } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSignIn = async () => {
    setLoading(true);
    await signIn();
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col">
      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Left - branding */}
          <div className="hidden lg:block animate-fade-in">
            <img src={logo} alt="GBL Tree" className="h-12 mb-6" />
            <h1 className="text-3xl font-normal text-[#202124] leading-tight mb-2">
              Education Loan
              <br />
              <span className="text-[#1A73E8] font-medium">Apply & Track</span>
            </h1>
            <p className="text-[#5F6368] text-base leading-relaxed mb-8">
              Your secure portal to apply, upload documents, and track your education loan application from submission to disbursement.
            </p>
            <div className="space-y-3">
              {FEATURES.map((f) => (
                <div key={f.title} className="flex items-center gap-3 bg-white rounded-lg p-4 border border-[#DADCE0]">
                  <div className="w-10 h-10 rounded-full bg-[#F8F9FA] flex items-center justify-center flex-shrink-0">
                    {f.icon}
                  </div>
                  <div>
                    <p className="font-medium text-[#202124] text-sm">{f.title}</p>
                    <p className="text-[#5F6368] text-xs mt-0.5">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - sign in card */}
          <div className="animate-slide-up">
            {/* Mobile logo */}
            <div className="lg:hidden text-center mb-6">
              <img src={logo} alt="GBL Tree" className="h-10 mx-auto mb-2" />
              <h2 className="text-lg font-normal text-[#202124]">Education Loan <span className="text-[#1A73E8] font-medium">Apply & Track</span></h2>
            </div>

            <div className="bg-white rounded-lg shadow-auth p-8 border border-[#DADCE0]">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-normal text-[#202124]">Welcome</h2>
                <p className="text-[#5F6368] text-sm mt-1">Sign in to access your loan portal</p>
              </div>

              {authError && (
                <div className="mb-6 p-3 rounded-lg bg-[#FCE8E6] border border-[#F5C6CB] flex items-start gap-2.5 animate-fade-in">
                  <svg className="w-4 h-4 text-[#D93025] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                  </svg>
                  <p className="text-[#D93025] text-sm">{authError}</p>
                </div>
              )}

              <button
                onClick={handleSignIn}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 bg-white hover:bg-[#F8F9FA] border border-[#DADCE0] hover:border-[#D2E3FC] text-[#3C4043] font-medium rounded-lg px-6 py-3 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-5 h-5 rounded-full border-2 border-[#DADCE0] border-t-[#1A73E8] animate-spin" />
                ) : (
                  <GoogleIcon />
                )}
                <span className="text-sm">{loading ? 'Signing in...' : 'Continue with Google'}</span>
              </button>

              <p className="text-center text-xs text-[#9AA0A6] mt-5 leading-relaxed">
                Use the same Google account email you shared with your GBL advisor
              </p>
            </div>

            <p className="text-center text-[#9AA0A6] text-xs mt-4">
              &copy; {new Date().getFullYear()} GBL Tree &middot; Education Loan Services
            </p>
          </div>
        </div>
      </div>

      {/* Bank logos auto-scroll at bottom */}
      <div className="border-t border-[#E8EAED] bg-white">
        <div className="max-w-5xl mx-auto">
          <BankLogoScroller />
        </div>
      </div>
    </div>
  );
}
